import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ApiService } from './core/services/api.service';
import { AuthService } from './core/services/auth.service';
import { LocaleCurrencyService } from './core/services/locale-currency.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { resetPageScrollPosition } from './core/utils/scroll.utils';
import { trackMetaPageView } from './core/utils/meta-pixel.util';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [MessageService]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Somewhere Fine';
  private destroy$ = new Subject<void>();
  /** Skip the first NavigationEnd — index.html already sent PageView on load. */
  private isFirstNavigation = true;
  private lastTrackedUrl: string | null = null;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private localeCurrencyService: LocaleCurrencyService
  ) {
    // Detect currency/country from the visitor's IP unless one is already chosen.
    this.localeCurrencyService.initLocaleFromIp();
    this.apiService.setSessionId().pipe(takeUntil(this.destroy$)).subscribe();
    this.apiService.getWebConfig().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        localStorage.setItem('auth_token', res?.data?.authorization_header?.split('Basic ')[1]);
        this.applyFaviconFromWebConfig(res?.data?.websiteimages_set);
        this.getDetails();
      },
      error: () => {
        // Auth bootstrap cannot proceed without webconfig; unblock guarded routes.
        this.authService.markAuthReady(false);
      },
    });

  }
  
  ngOnInit() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event) => {
      resetPageScrollPosition();
      requestAnimationFrame(() => resetPageScrollPosition());

      const url = event.urlAfterRedirects;
      if (this.isFirstNavigation) {
        this.isFirstNavigation = false;
        this.lastTrackedUrl = url;
        return;
      }
      if (url === this.lastTrackedUrl) {
        return;
      }
      this.lastTrackedUrl = url;
      trackMetaPageView();
    });
  }

    // Details API call — must settle before guarded routes (e.g. my-bookings) proceed.
  getDetails() {
    if(this.router.url.includes('set-password')) {
      this.authService.markAuthReady(false);
      return;
    }
    this.authService.getUserDetails().pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /** Prefer agency favicon from get_webconfig; keep static /favicon.ico as fallback. */
  private applyFaviconFromWebConfig(websiteImages: Array<{ category?: string; image_url?: string; image_type?: string }> | null | undefined): void {
    const favicon = websiteImages?.find((img) => img?.category === 'favicon' && img?.image_url);
    if (!favicon?.image_url) {
      return;
    }

    const type = favicon.image_type ? `image/${favicon.image_type}` : 'image/png';
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = type;
    link.href = favicon.image_url;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
